import { motion } from "framer-motion";
import ScrollFadeIn from "./Scrollfadein";
import { useIsMobile } from "./Useismobile";


import ProfilePic from "../assets/Dave1.jpg"

const roles = [
  { label: "FRONTEND DEVELOPER", sub: "2+ yrs · React, Tailwind, Web3", icon: "⟨/⟩" },
  { label: "UI/UX DESIGNER",      sub: "Beginner · Figma, User Research", icon: "◈" },
  { label: "PRODUCT MANAGER",     sub: "Certified · Udemy · Strategy & Delivery", icon: "◎" },
];

export default function Hero({ theme }) {
  const { fg, muted, border, accent, darkMode, bg } = theme;
  const isMobile = useIsMobile();

  return (
    <section style={{ padding: isMobile ? "48px 20px 40px" : "72px 40px 64px", maxWidth: 1200, margin: "0 auto" }}>

      {/* ── Title row ── */}
      <ScrollFadeIn>
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "flex-start",
          gap: isMobile ? 20 : 24,
        }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 10, letterSpacing: "0.2em", color: muted, marginBottom: isMobile ? 14 : 20 }}>
              IBADAN, NIGERIA — AVAILABLE FOR REMOTE
            </p>
            <h1 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? "clamp(56px, 18vw, 88px)" : "clamp(64px, 13vw, 160px)",
              lineHeight: 0.9,
              letterSpacing: "0.01em",
              fontWeight: 400,
              color: fg,
            }}>
              BUILDING<br />
              <span style={{ color: accent }}>DIGITAL</span><br />
              PRODUCTS
            </h1>
          </div>

          {/* Profile pic */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            style={{ flexShrink: 0 }}
          >
            <div style={{
              width: isMobile ? 72 : 160,
              height: isMobile ? 72 : 160,
              borderRadius: "50%",
              overflow: "hidden",
              border: `2px solid ${border}`,
            }}>
              <img
                src={ProfilePic}
                alt="David Olarinde"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                draggable={false}
              />
            </div>
          </motion.div>
        </div>
      </ScrollFadeIn>

      {/* ── Role cards ── */}
      <ScrollFadeIn delay={0.15}>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: 0,
          marginTop: isMobile ? 32 : 56,
          border: `1px solid ${border}`,
          borderRadius: 16,
          overflow: "hidden",
        }}>
          {roles.map(({ label, sub, icon }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{
                padding: isMobile ? "20px 20px" : "28px 28px",
                borderRight: (!isMobile && i < roles.length - 1) ? `1px solid ${border}` : "none",
                borderBottom: (isMobile && i < roles.length - 1) ? `1px solid ${border}` : "none",
                background: "transparent",
                position: "relative",
              }}
              whileHover={{ backgroundColor: darkMode ? "#111" : "#f5f5f0" }}
            >
              <div style={{ fontSize: 20, marginBottom: 10, color: accent }}>{icon}</div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: fg, marginBottom: 5 }}>
                {label}
              </div>
              <div style={{ fontSize: 11, color: muted, lineHeight: 1.6 }}>{sub}</div>
              {i === 2 && (
                <div style={{
                  position: "absolute", top: 16, right: 16,
                  fontSize: 8, letterSpacing: "0.1em",
                  padding: "3px 8px", border: `1px solid ${accent}`,
                  borderRadius: 100, color: accent,
                }}>
                  CERTIFIED
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </ScrollFadeIn>

      {/* ── Bio + CTAs ── */}
      <ScrollFadeIn delay={0.3}>
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "flex-end",
          gap: isMobile ? 20 : 24,
          marginTop: isMobile ? 32 : 48,
          paddingTop: isMobile ? 28 : 40,
          borderTop: `1px solid ${border}`,
        }}>
          <p style={{ fontSize: 13, lineHeight: 1.9, color: muted, maxWidth: 520 }}>
            I design, build, and ship digital products that people love using. My blend of
            engineering, design thinking, and product strategy means I can contribute across
            the full lifecycle — from idea to production — while keeping users at the centre.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a
              href="https://github.com/DaveInnovator"
              target="_blank"
              rel="noreferrer"
              style={{
                padding: "9px 20px", fontSize: 10, letterSpacing: "0.13em",
                border: `1px solid ${border}`, borderRadius: 100,
                background: "transparent", color: muted, textDecoration: "none",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = fg; e.currentTarget.style.color = fg; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = muted; }}
            >
              GITHUB ↗
            </a>
            <a
              href="mailto:olarindedavid49@gmail.com"
              style={{
                padding: "9px 20px", fontSize: 10, letterSpacing: "0.13em",
                border: `1px solid ${fg}`, borderRadius: 100,
                background: fg, color: bg, textDecoration: "none",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = "#000"; }}
              onMouseLeave={e => { e.currentTarget.style.background = fg; e.currentTarget.style.borderColor = fg; e.currentTarget.style.color = bg; }}
            >
              GET IN TOUCH
            </a>
          </div>
        </div>
      </ScrollFadeIn>
    </section>
  );
}